import React, { Suspense } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { trackLandingCTA } from '@/lib/analytics';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const DNAHelix = React.lazy(() =>
  import('../components/DNAHelix').then((m) => ({ default: m.DNAHelix }))
);

export interface HeroProps {
  onSignUp?: (tier?: string) => void;
}

/**
 * Marketing ruling M1 (2026-10-20), Phase A: process claims only. The AI
 * sentence lives in the sub-head, never the headline (R43(i)); no
 * performance figure, no live wording until Phases B/C.
 *
 * Implementation plan P0-09 (2026-10-22, R71 M5 "now"): the badge says the
 * product is in development and that we are looking for design-partner
 * managers, and the sub-head no longer says "pre-registered" -- the platform
 * has no pre-registration feature (PLATFORM_ENFORCEMENT_AUDIT section A).
 */
export const Hero: React.FC<HeroProps> = ({ onSignUp }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[hsl(225,20%,4%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_hsla(162,72%,46%,0.06)_0%,_transparent_60%)]" />

      {/* 3D DNA Helix -- purely decorative, so a WebGL failure (context
          creation can throw on browsers/devices with WebGL disabled,
          blocklisted GPUs, or too many contexts already open) must never
          take down the page. Contained locally rather than left to bubble
          up to ClerkProvider's app-wide boundary, which has already caught
          one unrelated canvas failure and shown users a confusing
          "unexpected error" screen for what should just be a silently
          missing background animation. */}
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <DNAHelix className="opacity-60" />
        </Suspense>
      </ErrorBoundary>

      {/* Content */}
      {/* Scrim: keeps the sub-head readable over the bright helix strands. */}
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_hsla(225,20%,4%,0.8)_0%,_hsla(225,20%,4%,0.45)_45%,_transparent_75%)]" />

      <div className="relative z-10 max-w-[900px] mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full border border-white/10 bg-white/[0.03] text-sm text-white/60">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2dd4a0] animate-pulse-glow" />
            In development. Looking for ten design-partner managers.
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="font-heading text-5xl md:text-[4rem] lg:text-[4.5rem] font-bold text-white leading-[1.08] mb-6 tracking-tight"
        >
          Your limits,{' '}
          <span className="text-gradient">enforced by code.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-lg md:text-xl text-white/50 max-w-[680px] mx-auto mb-10 leading-relaxed"
        >
          We are building a platform for systematic managers: run a program inside
          limits enforced by code, with every decision on the record. Today it runs a
          small library of documented, published rules on paper accounts, inside the
          risk limits you sign. An AI can propose the plan; it cannot place an order,
          raise a limit, or keep going after a halt. Every proposal, approval and order
          is recorded. It starts on a paper account, with no real money.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="flex gap-4 justify-center flex-wrap"
        >
          <Button size="lg" onClick={() => { trackLandingCTA('hero'); onSignUp?.('observer'); }} className="px-8 py-6 text-base glow-primary">
            Open a paper account
            <span className="ml-2">&rarr;</span>
          </Button>
          <a
            href="#how-it-works"
            className="inline-flex items-center px-8 py-3 text-base text-white/70 border border-white/10 rounded-lg hover:border-white/25 hover:text-white transition-all cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2dd4a0]"
          >
            See how it works
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-6 text-sm text-white/30"
        >
          No credit card. No real money is traded today. Long-only, no borrowing.
        </motion.p>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[hsl(225,20%,4%)] to-transparent" />
    </section>
  );
};
